import React from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "@/components/AppShell";
import { ShieldCheck, Mail } from "lucide-react";
import { CARE_NET_LOGO } from "@/lib/careNet";
import CareNetLink from "@/components/CareNetLink";

/**
 * Site-wide DPDP footer used on all public pages and inside AppShell.
 * Compact variant (`compact`) is used inside authenticated app pages.
 */
export default function Footer({ compact = false }) {
  if (compact) {
    return (
      <div className="mt-10 border-t border-border pt-6 pb-8 text-xs text-muted-foreground" data-testid="footer-compact">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2">
            <ShieldCheck size={12} className="text-primary" />
            <span>
              <strong className="text-primary">Data Fiduciary</strong> under India&apos;s DPDP Act, 2023 · Policy v2026-02-01
            </span>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <Link to="/directory" className="hover:text-primary" data-testid="footer-compact-directory">Provider Directory</Link>
            <Link to="/dpdp" className="hover:text-primary" data-testid="footer-compact-dpdp">DPDP rights</Link>
            <Link to="/privacy" className="hover:text-primary">Privacy</Link>
            <Link to="/terms" className="hover:text-primary">Terms</Link>
            <Link to="/grievance" className="hover:text-primary">Grievance</Link>
            <a href="mailto:dpo@arogyapramana.in" className="hover:text-primary inline-flex items-center gap-1">
              <Mail size={11} /> dpo@arogyapramana.in
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <footer className="border-t border-border bg-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <BrandMark />
          <p className="mt-3 text-xs text-muted-foreground max-w-sm">
            A <strong>Data Fiduciary</strong> under India&apos;s Digital Personal Data Protection Act, 2023.
            Arogya Pramana Trust Infrastructure Pvt. Ltd. · BKC, Mumbai.
          </p>
          <p className="mt-3 text-[11px] text-muted-foreground max-w-sm">
            You have the right to access, correct, and erase your data at any time.{" "}
            <Link to="/dpdp" className="text-primary underline">Read your DPDP rights</Link>.
          </p>
        </div>
        <div>
          <div className="overline mb-3">Product</div>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            <li><Link to="/directory" className="hover:text-primary">Directory</Link></li>
            <li><Link to="/network" className="hover:text-primary">Change Makers</Link></li>
            <li><Link to="/startups" className="hover:text-primary">Startups</Link></li>
            <li>
              <CareNetLink
                className="hover:text-primary inline-flex items-center gap-1.5"
                data-testid="footer-carenet"
              >
                <img src={CARE_NET_LOGO} alt="" className="h-3.5 w-3.5 object-contain" aria-hidden="true" />
                Care Net <span className="opacity-60">↗</span>
              </CareNetLink>
            </li>
          </ul>
        </div>
        <div>
          <div className="overline mb-3">DPDP & Legal</div>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            <li><Link to="/dpdp" className="hover:text-primary" data-testid="footer-dpdp">DPDP rights</Link></li>
            <li><Link to="/privacy" className="hover:text-primary" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-primary" data-testid="footer-terms">Terms of Service</Link></li>
            <li><Link to="/grievance" className="hover:text-primary" data-testid="footer-grievance">File a grievance</Link></li>
            <li><a href="mailto:dpo@arogyapramana.in" className="hover:text-primary">dpo@arogyapramana.in</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs text-muted-foreground">
          <div>© 2026 Arogya Pramana · Content protected · Unauthorised reproduction prohibited.</div>
          <div>DPO: Dr. Ananya Rao · Grievance SLA 30 days · Policy v2026-02-01</div>
        </div>
      </div>
    </footer>
  );
}
